import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { CalendarDevice, CalendarDeviceListResponse } from '@/types/scheduling-api';
import { activeDevices, orderDevices } from '@/scheduling/lib/device-columns';

/** Canonical key — the settings registry uses `['settings','resources-list',…]`; this is the calendar's own. */
export function calendarDevicesQueryKey(
  practiceId: string | null | undefined,
): readonly [string, string | null | undefined] {
  return ['calendar-devices', practiceId];
}

/** `MaxPageSize` in PracticeManagement's ResourceService — larger is clamped server-side. */
const PAGE_SIZE = 200;

/**
 * The active devices of a practice, ordered for the calendar's Devices tab
 * (subtype, then name) — PROD-2007.
 *
 *   GET /api/Resource?practiceId={id}&status=active&page={n}&pageSize=200
 *
 * Same wire the settings device form reads (`ResourceListResponse`, PM #944);
 * scheduling keeps its own hook + `CalendarDevice` type because domains never
 * cross-import. Pages are walked so a practice with >200 devices still gets
 * every column (dev's demo practice has 57).
 */
export function useCalendarDevices(practiceId: string | null | undefined, enabled = true) {
  return useQuery<CalendarDevice[]>({
    queryKey: calendarDevicesQueryKey(practiceId),
    queryFn: async ({ signal }) => {
      if (!practiceId) throw new Error('useCalendarDevices called without practiceId');
      const url = (page: number) =>
        `/api/Resource?practiceId=${encodeURIComponent(practiceId)}` +
        `&status=active&page=${page}&pageSize=${PAGE_SIZE}`;
      const first = await get<CalendarDeviceListResponse>(url(1), { signal });
      const resources = [...(first?.resources ?? [])];
      for (let page = 2; page <= (first?.totalPages ?? 1); page += 1) {
        const next = await get<CalendarDeviceListResponse>(url(page), { signal });
        resources.push(...(next?.resources ?? []));
      }
      // `status=active` already filters server-side; the client filter is the
      // belt to that brace so an archived row can never grow a column.
      return orderDevices(activeDevices(resources));
    },
    enabled: enabled && !!practiceId,
    staleTime: 30_000,
  });
}
